import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Mail, Clock, MapPin } from "lucide-react";
import { useRef } from "react";
import { ventures as staticVentures, maeTerra } from "@/data/portfolio";
import {
  RateCard,
  BookingActions,
  BookingMeta,
  StickyBookBar,
} from "@/components/Booking";

const paletteFor = (accent) => {
  switch (accent) {
    case "gold":
      return {
        bg: "bg-[#141210]",
        text: "text-cream",
        muted: "text-cream/75",
        border: "border-cream/15",
        accent: "text-gold",
        button:
          "bg-gold text-[#141210] hover:bg-cream hover:text-[#141210]",
        ghostButton:
          "border-cream/40 text-cream hover:bg-cream hover:text-[#141210]",
      };
    case "terracotta":
      return {
        bg: "bg-[#1c110d]",
        text: "text-cream",
        muted: "text-cream/75",
        border: "border-cream/15",
        accent: "text-terracotta",
        button:
          "bg-terracotta text-cream hover:bg-cream hover:text-[#1c110d]",
        ghostButton:
          "border-cream/40 text-cream hover:bg-cream hover:text-[#1c110d]",
      };
    case "forest":
    default:
      return {
        bg: "bg-forest",
        text: "text-cream",
        muted: "text-cream/75",
        border: "border-cream/15",
        accent: "text-gold",
        button: "bg-gold text-forest hover:bg-cream hover:text-forest",
        ghostButton:
          "border-cream/40 text-cream hover:bg-cream hover:text-forest",
      };
  }
};

export default function VentureDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const [venture, setVenture] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const v = staticVentures.find((x) => x.slug === slug);
    setVenture(v || null);
  }, [slug]);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.06, 1.2]);

  if (!venture) {
    return (
      <div
        className="min-h-screen bg-cream text-ink grid place-items-center"
        data-testid="venture-not-found"
      >
        <div className="text-center">
          <p className="chapter-num">404 · Not found</p>
          <h1 className="mt-4 font-display text-5xl">Venture not found</h1>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-8 px-6 py-3 border border-ink text-ink label hover:bg-ink hover:text-cream"
          >
            Back to studio
          </button>
        </div>
      </div>
    );
  }

  const p = paletteFor(venture.accent);
  const idx = staticVentures.findIndex((x) => x.slug === venture.slug);
  const nextV = staticVentures[(idx + 1) % staticVentures.length];

  return (
    <main
      className={`${p.bg} ${p.text} no-x`}
      data-testid="venture-page"
    >
      {/* Big cinematic hero */}
      <section
        ref={heroRef}
        className="relative min-h-[100vh] overflow-hidden"
      >
        {/* A photographic hero only where the photograph can carry it. Where
            heroPhotographic is false the source is a scraped thumbnail and
            stretching it here is a five times upscale at dpr 2, so the hero
            becomes a type panel with a soft ground instead. */}
        {venture.heroPhotographic === false ? (
          <>
            <div className="absolute inset-0 bg-ink" />
            <div
              className="absolute inset-0 opacity-[0.55]"
              style={{
                background:
                  "radial-gradient(120% 90% at 15% 0%, rgba(181,138,58,0.30) 0%, transparent 55%), radial-gradient(100% 80% at 100% 100%, rgba(184,92,56,0.28) 0%, transparent 60%)",
              }}
            />
          </>
        ) : (
          <>
            <motion.img
              src={venture.hero}
              alt={venture.title}
              style={reduceMotion ? undefined : { y: bgY, scale: bgScale }}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className={`absolute inset-0 ${venture.accent === "forest" ? "bg-forest/55" : "bg-ink/55"}`} />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(60% 55% at 50% 45%, transparent 20%, rgba(0,0,0,0.75) 100%)",
              }}
            />
          </>
        )}
        <div className="relative z-10 h-full mx-auto max-w-[1600px] px-6 md:px-10 pt-32 pb-16 flex flex-col justify-between min-h-[100vh]">
          <div className="flex items-start justify-between">
            <Link
              to="/"
              className={`inline-flex items-center gap-2 label ${p.muted} editorial-link`}
              data-testid="venture-back"
              data-cursor="hover"
            >
              <ArrowLeft size={14} /> Back to studio
            </Link>
            <span className={`label ${p.muted}`}>
              Venture · 0{idx + 1}
            </span>
          </div>

          <div className="max-w-[1400px]">
            <p className={`label ${p.accent}`}>
              {venture.role}
            </p>
            <h1
              className="mt-6 font-display leading-[0.9] tracking-tight"
              style={{ fontSize: "min(14vw, 12rem)" }}
              data-testid="venture-title"
            >
              {venture.title}
            </h1>
            <p className={`mt-6 font-sans font-light text-xl md:text-2xl ${p.muted} max-w-[52ch] leading-relaxed`}>
              {venture.tagline}
            </p>
            <div className={`mt-8 flex flex-wrap items-center gap-x-4 gap-y-2 label ${p.muted}`}>
              <span>{venture.location}</span>
              <span aria-hidden="true">·</span>
              <span>{venture.year}</span>
              {venture.attribution && (
                <>
                  <span aria-hidden="true">·</span>
                  <span>{venture.attribution}</span>
                </>
              )}
            </div>

            {/* Book, above the fold */}
            <BookingActions
              venture={venture}
              tone="dark"
              size="lg"
              className="mt-10"
            />
            <BookingMeta venture={venture} tone="dark" className="mt-4" />
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-10 py-24 md:py-32 grid grid-cols-12 gap-6 md:gap-10">
        <div className="col-span-12 md:col-span-4">
          <p className={`chapter-num ${p.muted}`}>About the venture</p>
        </div>
        <div className="col-span-12 md:col-span-8">
          <p className="font-display text-3xl md:text-4xl leading-[1.2]">
            {venture.description}
          </p>
        </div>
      </section>

      {/* Rates and how to book */}
      {venture.bookable && (
        <section
          className={`border-t ${p.border} mx-auto max-w-[1600px] px-6 md:px-10 py-16 md:py-20`}
          data-testid="venture-booking-panel"
        >
          <div className="grid grid-cols-12 gap-10 md:gap-14">
            <div className="col-span-12 md:col-span-7">
              <p className={`chapter-num ${p.muted}`}>
                {venture.rates.length > 0 ? "Published rates" : "How to book"}
              </p>
              {venture.rates.length > 0 ? (
                <RateCard rates={venture.rates} className="mt-7" />
              ) : (
                <p className="mt-6 font-display text-2xl md:text-3xl leading-[1.25] max-w-[34ch]">
                  Dates and rates come back by return. The house takes one
                  booking at a time.
                </p>
              )}
              <BookingActions
                venture={venture}
                tone="dark"
                size="lg"
                className="mt-10"
              />
            </div>

            <dl className="col-span-12 md:col-span-4 md:col-start-9 space-y-5">
              {venture.contact.hours && (
                <div className={`border-t ${p.border} pt-4 flex gap-3`}>
                  <Clock size={14} className={`mt-1 shrink-0 ${p.accent}`} />
                  <div>
                    <dt className={`label ${p.muted}`}>
                      Hours
                    </dt>
                    <dd className="mt-1 font-sans text-sm">
                      {venture.contact.hours}
                    </dd>
                  </div>
                </div>
              )}
              {venture.contact.email && (
                <div className={`border-t ${p.border} pt-4 flex gap-3`}>
                  <Mail size={14} className={`mt-1 shrink-0 ${p.accent}`} />
                  <div className="min-w-0">
                    <dt className={`label ${p.muted}`}>
                      Email
                    </dt>
                    <dd className="mt-1 font-sans text-sm break-words">
                      <a
                        href={`mailto:${venture.contact.email}`}
                        className="editorial-link"
                        data-cursor="hover"
                        data-testid={`venture-email-${venture.slug}`}
                      >
                        {venture.contact.email}
                      </a>
                    </dd>
                  </div>
                </div>
              )}
              {venture.contact.address && (
                <div className={`border-t ${p.border} pt-4 flex gap-3`}>
                  <MapPin size={14} className={`mt-1 shrink-0 ${p.accent}`} />
                  <div>
                    <dt className={`label ${p.muted}`}>
                      Address
                    </dt>
                    <dd className="mt-1 font-sans text-sm">
                      {venture.contact.address}
                    </dd>
                  </div>
                </div>
              )}
            </dl>
          </div>
        </section>
      )}

      {/* Mae Terra carried a home page interlude with three chapters about the
          house. The interlude retired with the one page layout, so the chapters
          live here, on the page about the place, in the same ruled rows. */}
      {venture.slug === "mae-terra" && (
        <section
          className={`border-t ${p.border} mx-auto max-w-[1600px] px-6 md:px-10 py-16 md:py-20`}
          data-testid="mae-terra-chapters"
        >
          <p className={`chapter-num ${p.muted}`}>The house</p>
          <ul className="mt-7">
            {maeTerra.chapters.map((c, i) => (
              <li
                key={c.h}
                className={`grid grid-cols-12 gap-x-6 gap-y-2 border-t ${p.border} py-7 last:border-b`}
              >
                <p className={`col-span-12 md:col-span-1 label ${p.accent} md:pt-1.5`}>
                  0{i + 1}
                </p>
                <h3 className="col-span-12 md:col-span-4 font-display text-display-s">
                  {c.h}
                </h3>
                <p className={`col-span-12 md:col-span-7 font-sans font-light text-base ${p.muted} leading-relaxed`}>
                  {c.p}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* What it is */}
      <section className={`border-t ${p.border} mx-auto max-w-[1600px] px-6 md:px-10 py-16 md:py-20 grid grid-cols-2 md:grid-cols-4 gap-8`}>
        {venture.facts.map((h, i) => (
          <div key={h} className={`border-t ${p.border} pt-4`}>
            <p className={`label ${p.muted}`}>
              0{i + 1}
            </p>
            <p className="mt-3 font-display text-xl md:text-2xl leading-[1.15]">
              {h}
            </p>
          </div>
        ))}
      </section>

      {/* Gallery */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-10 pb-24 md:pb-32 grid grid-cols-12 gap-4 md:gap-6">
        {venture.gallery.map((src, i) => {
          const shapes = [
            "col-span-12 md:col-span-8 aspect-[16/10]",
            "col-span-12 md:col-span-4 aspect-[4/5]",
            "col-span-6 md:col-span-4 aspect-[4/5]",
            "col-span-6 md:col-span-4 aspect-[4/5]",
            "col-span-12 md:col-span-4 aspect-[4/5]",
            "col-span-12 aspect-[16/7]",
          ];
          return (
            <motion.div
              key={src + i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 0.9, delay: i * 0.05 }}
              className={`img-frame ${shapes[i % shapes.length]}`}
              data-testid={`venture-gallery-${i}`}
            >
              <img src={src} alt={`${venture.title} ${i + 1}`} loading="lazy" />
            </motion.div>
          );
        })}
      </section>

      {/* Next venture */}
      {nextV && (
        <Link
          to={`/ventures/${nextV.slug}`}
          className={`block border-t ${p.border} relative group overflow-hidden`}
          data-testid="venture-next"
          data-cursor="view"
        >
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
            <img
              src={nextV.hero}
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/60" />
          </div>
          <div className="relative mx-auto max-w-[1600px] px-6 md:px-10 py-24 md:py-32 flex items-center justify-between">
            <div>
              <p className={`label ${p.muted}`}>
                Next Venture · 0{((idx + 1) % staticVentures.length) + 1}
              </p>
              <h2 className={`mt-4 font-display text-6xl md:text-8xl leading-[0.95] ${p.text}`}>
                {nextV.title}.
              </h2>
            </div>
            <ArrowUpRight
              size={64}
              className={`${p.text} transition-transform group-hover:rotate-45`}
            />
          </div>
        </Link>
      )}

      <StickyBookBar venture={venture} />
    </main>
  );
}
